import React, { useEffect, useState } from 'react';

import { Card, Pagination } from '../components';
import { SearchIcon, XIcon } from '@heroicons/react/solid';
import { allJobs } from '../utils/Data';
// Uncached client: the employer's own list must reflect renewals and new posts immediately.
import { clientNoCdn as client } from '../clientNoCdn';
import { useAuth } from '../contexts/AuthContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const PAGE_SIZE = 10;

// Case-insensitive match on title, company name or city.
function matchesSearch(job, term) {
  if (!term) return true;
  return [job.title, job.companyName, job.city].some((field) =>
    (field || '').toLowerCase().includes(term)
  );
}

function Jobs({ onExtendClick, onRenewClick, refreshTrigger }) {
  const [currentPage, setCurrentPage] = useState(0);
  const { currentUser } = useAuth();

  const [tab, setTab] = useState('active');
  const [search, setSearch] = useState('');

  const [activeJobs, setActiveJobs] = useState([]);
  const [expiredJobs, setExpiredJobs] = useState([]);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    client
      .fetch(allJobs(currentUser.uid))
      .then((jobs) => {
        const currentDate = new Date();
        setActiveJobs(jobs.filter((job) => new Date(job.postExpiry) >= currentDate));
        setExpiredJobs(jobs.filter((job) => new Date(job.postExpiry) < currentDate));

        // Expiry reminder emails link to /dashboard?extend=<jobId> (REV-004): open Extend for a
        // live job or Renew for an expired one, then drop the parameter so a refresh doesn't reopen it.
        const extendId = new URLSearchParams(location.search).get('extend');
        const job = extendId && jobs.find((j) => j._id === extendId);
        if (job) {
          const data = { jobId: job._id, title: job.title, postedOn: job.postedOn, postAt: job.postAt, postExpiry: job.postExpiry };
          if (new Date(job.postExpiry) < currentDate) {
            setTab('expired');
            if (onRenewClick) onRenewClick(data);
          } else if (onExtendClick) {
            onExtendClick(data);
          }
          navigate('/dashboard', { replace: true });
        }
      })
      .catch((err) => console.log(err));
  }, [refreshTrigger]);

  const term = search.trim().toLowerCase();
  const tabJobs = tab === 'active' ? activeJobs : expiredJobs;
  const visibleJobs = tabJobs.filter((job) => matchesSearch(job, term));
  const maxOpenings = visibleJobs.length;

  function activePageinc() {
    if (currentPage < maxOpenings / PAGE_SIZE - 1) setCurrentPage(currentPage + 1);
  }

  function activePagedec() {
    if (currentPage != 0) {
      setCurrentPage(currentPage - 1);
    }
  }

  function selectTab(next) {
    setTab(next);
    setCurrentPage(0);
  }

  function onSearchChange(value) {
    setSearch(value);
    setCurrentPage(0);
  }

  const JobCardRender = (job) => {
    const currentDate = new Date();
    const expiryDate = new Date(job.postExpiry);
    const isJobExpired = expiryDate < currentDate;

    return (
      <Card
        key={job._id}
        jobId={job._id}
        link={`/dashboard/update/${job._id}`}
        recordLink={`/dashboard/record/${job._id}`}
        applicantsLink={`/dashboard/applications/${job._id}`}
        applicantCount={job.applications}
        jobExpired={isJobExpired}
        title={job.title}
        companyName={job.companyName}
        city={job.city}
        postedOn={job.postedOn}
        postAt={job.postAt}
        postExpiry={job.postExpiry}
        onExtendClick={onExtendClick}
        onRenewClick={onRenewClick}
      />
    );
  };

  const NoJob = () => {
    if (term && tabJobs.length > 0) {
      return (
        <section className="flex flex-col items-center mt-16 text-center">
          <p className="font-bold text-lg">
            No {tab} jobs match “{search.trim()}”
          </p>
          <button
            type="button"
            onClick={() => onSearchChange('')}
            className="mt-3 text-primary font-semibold hover:underline"
          >
            Clear search
          </button>
        </section>
      );
    }

    const hasExpiredJobs = expiredJobs.length > 0;
    const message =
      tab === 'active'
        ? hasExpiredJobs
          ? 'No Active Jobs – Post a New Job!'
          : 'No Active Jobs – Post Your First Job Today!'
        : 'No Expired Jobs';

    return (
      <section className="justify-center flex flex-col items-center mt-16 ">
        <div className="">
          <p className="font-bold text-lg">{message}</p>
        </div>
        {tab === 'active' && (
          <>
            <p className="text-gray-600 text-sm mt-2 mb-4 text-center max-w-lg">
              Reach thousands of qualified candidates across 5 specialized job
              boards
            </p>
            <Link to="/post-job">
              <button className="px-4 py-1 bg-primary font-semibold text-white border-2 border-primary hover:text-primary hover:bg-white ease-in duration-75 rounded-md">
                Post a Job
              </button>
            </Link>
          </>
        )}
      </section>
    );
  };

  const tabClass = (name) =>
    `px-4 py-1 rounded font-semibold hover:bg-gray-200 ${tab === name ? 'bg-gray-200' : ''}`;

  return (
    <div className="max-w-6xl mx-auto w-full px-4 sm:px-6 scroll-smooth flex-grow flex flex-col">
      {/* Toolbar: tabs on the left, search on the right; stacks on small screens */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pb-6">
        <div className="flex space-x-2 text-lg text-primary justify-center md:justify-start">
          <button id="active" onClick={() => selectTab('active')} className={tabClass('active')}>
            Active Jobs ({activeJobs.length})
          </button>
          <button id="expired" onClick={() => selectTab('expired')} className={tabClass('expired')}>
            Expired Jobs ({expiredJobs.length})
          </button>
        </div>

        <div className="relative w-full md:w-80">
          <SearchIcon
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400"
            aria-hidden="true"
          />
          <input
            type="search"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search by title, company or city"
            aria-label="Search your jobs by title, company or city"
            className="w-full rounded-md border-gray-300 pl-10 pr-9 py-2 text-sm focus:ring-primary focus:border-primary"
          />
          {search && (
            <button
              type="button"
              onClick={() => onSearchChange('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
            >
              <span className="sr-only">Clear search</span>
              <XIcon className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      {/* The list grows to fill the page so pagination always sits at the bottom */}
      <section className="pb-4 flex-grow flex flex-col">
        <div className="mb-8 flex-grow">
          {visibleJobs.length >= 1
            ? visibleJobs
                .slice(currentPage * PAGE_SIZE, currentPage * PAGE_SIZE + PAGE_SIZE)
                .map(JobCardRender)
            : NoJob()}
        </div>

        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          increment={activePageinc}
          decrement={activePagedec}
          maxOpenings={maxOpenings}
        />
      </section>
    </div>
  );
}

export default Jobs;
